import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ethers } from 'ethers';
import { provider, ethersUtils, createContract, getAccounts, initializeEthers } from './EthersSetup';
import Mill from '../deployedContractsInfo/Mill.json';
import GameMakerConfig from '../deployedContractsInfo/GameMaker.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import { AddressDisplayLong } from './AddressDisplay.js';
import { TransactionLink } from './LinkDisplay';
import blockchainDataManager from '../services/BlockchainDataManager.js';
import TopRightBar from './basics/TopRightBar';
import GameTransactionHistory from './basics/GameTransactionHistory';

const MillGame = () => {
  const { gameID = '1' } = useParams();
  const [gameState, setGameState] = useState({
    player1: '',
    player2: '',
    currentPlayer: '',
    status: '',
    gameFinished: false,
    lastMoveTime: 0,
    contribution1: 0,
    contribution2: 0,
    board: Array(24).fill('0x0000000000000000000000000000000000000000'),
    winner: '',
    phase: 0, // 0 = Placement, 1 = Movement
    player1Pieces: 0,
    player2Pieces: 0,
    player1PlacedPieces: 0,
    player2PlacedPieces: 0,
    firstPlayer: ''
  });

  // Ref to store current game state for event handlers (prevents stale closures)
  const gameStateRef = useRef(gameState);

  const [userState, setUserState] = useState({ userAddress: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [moveFromPosition, setMoveFromPosition] = useState(null);
  const [captureMode, setCaptureMode] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [captureablePositions, setCaptureablePositions] = useState([]);
  const [lastMovePositions, setLastMovePositions] = useState([]);
  const [lastMoveType, setLastMoveType] = useState(''); // 'move', 'place', 'capture'

  // Memoized game events for GameTransactionHistory to prevent constant reloading
  const millGameEvents = useMemo(() => [
    { name: 'PiecePlaced', label: 'Piece Placed' },
    { name: 'PieceMoved', label: 'Piece Moved' },
    { name: 'MillFormed', label: 'Mill Formed' },
    { name: 'PieceCaptured', label: 'Piece Captured' }
  ], []);
  const [showLastMoveIndicators, setShowLastMoveIndicators] = useState(false);
  const [transactionPending, setTransactionPending] = useState(null); // { type: 'move'|'place', from?: number, to?: number, message: string }
  const [justCompletedMove, setJustCompletedMove] = useState(false); // Temporary state to prevent "your turn" flash after move

  // Board position coordinates for SVG rendering (24 positions in 3 concentric squares)
  const getBoardPositions = () => {
    const positions = [];
    const centerX = 200;
    const centerY = 200;
    
    // Outer square (0-7)
    const outerSize = 150;
    positions.push(
      { x: centerX - outerSize, y: centerY - outerSize }, // 0
      { x: centerX, y: centerY - outerSize },              // 1
      { x: centerX + outerSize, y: centerY - outerSize }, // 2
      { x: centerX + outerSize, y: centerY },              // 3
      { x: centerX + outerSize, y: centerY + outerSize }, // 4
      { x: centerX, y: centerY + outerSize },              // 5
      { x: centerX - outerSize, y: centerY + outerSize }, // 6
      { x: centerX - outerSize, y: centerY }               // 7
    );
    
    // Middle square (8-15)
    const middleSize = 100;
    positions.push(
      { x: centerX - middleSize, y: centerY - middleSize }, // 8
      { x: centerX, y: centerY - middleSize },              // 9
      { x: centerX + middleSize, y: centerY - middleSize }, // 10
      { x: centerX + middleSize, y: centerY },              // 11
      { x: centerX + middleSize, y: centerY + middleSize }, // 12
      { x: centerX, y: centerY + middleSize },              // 13
      { x: centerX - middleSize, y: centerY + middleSize }, // 14
      { x: centerX - middleSize, y: centerY }               // 15
    );
    
    // Inner square (16-23)
    const innerSize = 50;
    positions.push(
      { x: centerX - innerSize, y: centerY - innerSize }, // 16
      { x: centerX, y: centerY - innerSize },              // 17
      { x: centerX + innerSize, y: centerY - innerSize }, // 18
      { x: centerX + innerSize, y: centerY },              // 19
      { x: centerX + innerSize, y: centerY + innerSize }, // 20
      { x: centerX, y: centerY + innerSize },              // 21
      { x: centerX - innerSize, y: centerY + innerSize }, // 22
      { x: centerX - innerSize, y: centerY }               // 23
    );
    
    return positions;
  };

  const boardPositions = getBoardPositions();

  // Adjacency matrix from Mill.sol contract - position -> adjacent positions
  // UPDATED to exactly match contract Mill.sol lines 69-99
  const adjacencies = {
    // Outer square (0-7)
    0: [1, 7],        // left top
    1: [0, 2, 9],     
    2: [1, 3],
    3: [2, 4, 11],
    4: [3, 5],
    5: [4, 6, 13],
    6: [5, 7],
    7: [0, 6, 15],
    
    // Middle square (8-15)  
    8: [9, 15],  
    9: [1, 8, 10, 17],
    10: [9, 11],
    11: [3, 10, 12, 19],
    12: [11, 13],
    13: [5, 12, 14, 21],
    14: [13, 15],
    15: [7, 8, 14, 23],
    
    // Inner square (16-23)
    16: [17, 23],     // connects to next, prev
    17: [9, 16, 18],
    18: [17, 19],
    19: [11, 18, 20],
    20: [19, 21],
    21: [13, 20, 22],
    22: [21, 23],
    23: [15, 16, 22]
  };

  // Helper function to check if two positions are adjacent
  const isAdjacent = (from, to) => {
    return adjacencies[from] && adjacencies[from].includes(to);
  };

  // Helper function to get valid move targets for a selected piece
  const getValidMoveTargets = (fromPosition) => {
    if (fromPosition === null || fromPosition === undefined) return [];
    
    const currentPlayerAddress = userState.userAddress?.toLowerCase();
    const isPlayer1 = gameState.player1.toLowerCase() === currentPlayerAddress;
    const playerPieces = isPlayer1 ? gameState.player1Pieces : gameState.player2Pieces;
    
    // Wild rule: if player has exactly 3 pieces, can move anywhere (must match contract logic)
    const canMoveAnywhere = playerPieces === 3;
    
    const validTargets = [];
    for (let i = 0; i < 24; i++) {
      // Must be empty position
      if (gameState.board[i] === '0x0000000000000000000000000000000000000000') {
        if (canMoveAnywhere || isAdjacent(fromPosition, i)) {
          validTargets.push(i);
        }
      }
    }
    
    return validTargets;
  };

  const formatEth = (wei) => {
    if (!wei) return '0';
    try {
      return ethersUtils.formatEther(wei.toString());
    } catch (error) {
      console.error('Error formatting POL:', error);
      return '0';
    }
  };

  const loadGameData = async (isInitialLoad = false, forceRefresh = false, contractInstance = null) => {
    try {
      // Use provided contract instance or stored state, only create fresh if necessary
      let millContractToUse = contractInstance || millContract;

      if (!millContractToUse) {
        console.log('[Mill] No contract available, creating fresh instance');
        millContractToUse = await createContract(contractAddresses.Mill, Mill.abi);
      }

      if (forceRefresh) {
        console.log(`[Mill] Force refresh - using ${contractInstance ? 'provided' : 'stored'} contract`);
      }
      if (isInitialLoad) {
        setLoading(true);
      }
      
      const game = await millContractToUse.games(gameID);
      const player1 = game.player1;
      const player2 = game.player2;
      const status = game.status.toString();
      const currentPlayer = (status === '0' || status === '1') ? (status === '0' ? player1 : player2) : '';
      const gameFinished = (status === '2' || status === '3' || status === '4' || status === '5');
      const lastMoveTime = Number(game.lastMoveTime);
      const contribution1 = game.contribution1;
      const contribution2 = game.contribution2;
      
      const board = await millContractToUse.getBoard(gameID);
      const gameStateInfo = await millContractToUse.getGameState(gameID);
      
      console.log(`[Mill] Game data loaded - Status: ${status}, Block: ${await provider.getBlockNumber()}`);

      let winner = '';
      if (status === '3') {
        winner = player1;
      } else if (status === '4') {
        winner = player2;
      } else if (status === '2') {
        winner = 'draw';
      }

      // Get first player from GameStarted events
      let firstPlayer = '';
      try {
        const startEvents = await millContractToUse.queryFilter(
          millContractToUse.filters.GameStarted(Number(gameID)),
          0,
          'latest'
        );
        if (startEvents.length > 0) {
          firstPlayer = startEvents[0].args.firstPlayer;
        }
      } catch (error) {
        console.error('Error loading first player:', error);
      }

      const newGameState = { 
        player1, 
        player2, 
        currentPlayer, 
        status, 
        gameFinished, 
        lastMoveTime, 
        contribution1, 
        contribution2, 
        board,
        winner,
        phase: Number(gameStateInfo.phase),
        player1Pieces: Number(gameStateInfo.player1Pieces),
        player2Pieces: Number(gameStateInfo.player2Pieces),
        player1PlacedPieces: Number(gameStateInfo.player1PlacedPieces),
        player2PlacedPieces: Number(gameStateInfo.player2PlacedPieces),
        firstPlayer
      };

      setGameState(prevState => {
        const hasChanged = !prevState.player1 || 
          prevState.status !== status || 
          prevState.lastMoveTime !== lastMoveTime ||
          JSON.stringify(prevState.board) !== JSON.stringify(board) ||
          prevState.phase !== newGameState.phase;
        
        if (hasChanged || forceRefresh) {
          console.log('Mill game state changed, updating...');
          return newGameState;
        } else {
          console.log('No Mill game state changes detected');
          return prevState;
        }
      });

      
      if (isInitialLoad) {
        setLoading(false);
      }
    } catch (error) {
      if (error.message.includes("No wallet connected")) {
        console.log('Mill: Wallet not connected yet, skipping game data load');
        setErrorMessage('Connecting to wallet...');
      } else if (error.message.includes("Game with ID doesn't exist")) {
        setErrorMessage("Game with ID doesn't exist");
      } else {
        console.error('Failed to load Mill game data:', error);
        setErrorMessage('Failed to load game data');
      }
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };


  const [millContract, setMillContract] = useState(null);
  const [ethersContract, setEthersContract] = useState(null);
  const [gameMakerContract, setGameMakerContract] = useState(null);

  // Create GameMaker contract for transaction history
  useEffect(() => {
    const initGameMakerContract = async () => {
      try {
        const contract = await createContract(contractAddresses.GameMaker, GameMakerConfig.abi);
        setGameMakerContract(contract);
      } catch (error) {
        console.error('Mill: Failed to create GameMaker contract:', error);
      }
    };
    initGameMakerContract();
  }, []);

  useEffect(() => {
    const initWallet = async () => {
      try {
        await initializeEthers();
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserState({ userAddress: accounts[0] });
        }
        setIsWalletInitialized(true);
      } catch (error) {
        console.error('Mill: Failed to initialize wallet:', error);
        setIsWalletInitialized(true);
      }
    };
    initWallet();
  }, []);

  // Set browser tab title for the game
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `Mill #${gameID}`;
    
    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, [gameID]);

  useEffect(() => {
    if (!isWalletInitialized) return;

    const initializeContract = async () => {
      try {
        const contract = await createContract(contractAddresses.Mill, Mill.abi);
        setMillContract(contract);

        // Create ethers contract for events using the provider from EthersSetup
        if (provider) {
          const ethersContract = new ethers.Contract(contractAddresses.Mill, Mill.abi, provider);
          setEthersContract(ethersContract);
          console.log('🔧 Mill: Ethers contract created for events');
        } else {
          console.log('🔧 Mill: No provider available for ethers contract');
        }

        await loadGameData(true, false, contract);
        if (errorMessage === 'Connecting to wallet...') {
          setErrorMessage('');
        }
      } catch (error) {
        console.error('Error creating Mill contract:', error);
        setMillContract(null);
        setEthersContract(null);
      }
    };

    initializeContract();
  }, [gameID, isWalletInitialized]);

  // Keep gameStateRef updated for event handlers
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Auto-pause polling when game finishes
  useEffect(() => {
    const isGameFinished = gameState.gameFinished === true;
    window.dispatchEvent(new CustomEvent('setPollingPause', {
      detail: {
        isPaused: isGameFinished,
        reason: isGameFinished ? 'game-finished' : ''
      }
    }));
  }, [gameState.gameFinished]);


  // Wallet handlers for TopRightBar
  const handleSwitchWallet = () => {
    // For individual game pages, we'll navigate back to main page for wallet switching
    window.location.href = '/';
  };

  const handleWalletDisconnect = async () => {
    try {
      const accounts = await getAccounts();
      setUserState({ userAddress: accounts[0] || '' });
    } catch (error) {
      console.error('Error after wallet disconnect:', error);
      window.location.href = '/';
    }
  };

  const handleGameUpdate = useCallback(async () => {
    console.log('[Mill] Event detected, refreshing game state...');

    // Run data loading and transaction info in parallel for better performance
    const currentGameState = gameStateRef.current;
    const promises = [
      loadGameData(false, false) // Refresh without loading state
    ];


    await Promise.all(promises);

    // Clear transaction pending state when blockchain event confirms action
    setTransactionPending(null);

    // Clear the "just completed move" flag now that we have fresh game state
    setJustCompletedMove(false);

    // Clear any lingering error messages when game state updates
    setErrorMessage('');
  }, [gameID]); // Only gameID dependency - no more stale closure issues

  const handlePiecePlaced = useCallback(async (gameId, player, position, event) => {
    console.log('[Mill] PiecePlaced event detected:', { gameId, player, position });
    
    // Update game state first, then set visual indicators
    await handleGameUpdate();
    
    // Set visual indicators after board state is updated
    setLastMovePositions([Number(position)]);
    setLastMoveType('place');
    setShowLastMoveIndicators(true);
  }, [handleGameUpdate]);

  const handlePieceMoved = useCallback(async (gameId, player, from, to, event) => {
    console.log('[Mill] PieceMoved event detected:', { gameId, player, from, to });
    
    // Update game state first, then set visual indicators
    await handleGameUpdate();
    
    // Set visual indicators after board state is updated
    setLastMovePositions([Number(from), Number(to)]);
    setLastMoveType('move');
    setShowLastMoveIndicators(true);
  }, [handleGameUpdate]);

  const handlePieceCaptured = useCallback(async (gameId, capturingPlayer, capturedFrom, position, event) => {
    console.log('[Mill] PieceCaptured event detected:', { gameId, capturingPlayer, capturedFrom, position });
    
    // Update game state first (this updates the board with captured piece removed)
    await handleGameUpdate();
    
    // Add to existing positions for atomic mill+capture operations
    // Mark this as a capture (not a move) by adding a special flag
    setLastMovePositions(prev => [...prev, Number(position)]);
    setLastMoveType('capture'); // Track that this included a capture
    setShowLastMoveIndicators(true);
  }, [handleGameUpdate]);

  // Convert gameID to number for event filtering consistency
  const gameIDNumber = Number(gameID);

  // Set up polling system for Mill page (2 contracts: GameMaker + Mill)
  useEffect(() => {
    if (!provider || !ethersContract) return;

    // Configure polling for this game page - ensure full setup
    const setupPolling = async () => {
      try {
        // Initialize the complete polling system
        await blockchainDataManager.setupSmartPolling();

        // Add Mill to the polled contracts (GameMaker is already included)
        blockchainDataManager.addToPolledContracts('Mill');

        console.log('[Mill] Polling system fully initialized with 2 contracts');
      } catch (error) {
        console.error('[Mill] Failed to setup polling system:', error);
      }
    };

    setupPolling();

    // Subscribe to game updates from polling system
    const handleGameInstanceUpdate = (data) => {
      // Filter updates for this specific game
      if (data.source === 'polling' && (data.contract === 'Mill' || data.contract === 'GameMaker')) {
        console.log(`[Mill] Polling update from ${data.contract}, refreshing game state...`);
        handleGameUpdate();
      }
    };

    const unsubscribe = blockchainDataManager.subscribe('gameInstancesUpdated', handleGameInstanceUpdate);

    return unsubscribe;
  }, [provider, ethersContract, gameID, handleGameUpdate]);

  // Monitor contract state changes for debugging
  useEffect(() => {
    console.log('📋 Mill: Contract state changed:', {
      hasMainContract: !!millContract,
      hasEthersContract: !!ethersContract
    });
  }, [millContract, ethersContract]);


  const handlePositionClick = async (position) => {
    if (!userState.userAddress) {
      setErrorMessage('User address not available. Have you connected your wallet?');
      return;
    }

    if (gameState.gameFinished) {
      setErrorMessage('Game is already finished');
      return;
    }

    if (gameState.currentPlayer.toLowerCase() !== userState.userAddress.toLowerCase()) {
      setErrorMessage('It\'s not your turn');
      return;
    }

    // Clear last move indicators when current player starts their turn
    if (showLastMoveIndicators) {
      setShowLastMoveIndicators(false);
      setLastMovePositions([]);
      setLastMoveType('');
    }

    try {
      // Reuse existing contract or create fresh one only if needed
      let millContractToUse = millContract;
      if (!millContractToUse) {
        console.log('[Mill] No contract available for position click, creating fresh instance');
        millContractToUse = await createContract(contractAddresses.Mill, Mill.abi);
      }

      // If in capture mode, handle capture selection
      if (captureMode) {
        if (!captureablePositions.includes(position)) {
          setErrorMessage('You cannot capture this piece');
          return;
        }
        
        // Execute the pending move with capture
        await executeMoveWithCapture(pendingMove, position);
        return;
      }

      // Placement phase
      if (gameState.phase === 0) {
        if (gameState.board[position] !== '0x0000000000000000000000000000000000000000') {
          setErrorMessage('Position is already occupied');
          return;
        }

        // Check if this placement would form a mill
        const wouldFormMill = await checkWouldFormMill(position, null);
        
        if (wouldFormMill) {
          // Get capturable positions
          const userIsPlayer1 = gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
          const capturable = await millContractToUse.getCaptureablePositions(gameID, userIsPlayer1);
          
          if (capturable.length > 0) {
            // Enter capture mode
            setPendingMove({ type: 'place', position });
            setCaptureablePositions(capturable.map(pos => Number(pos)));
            setCaptureMode(true);
            setErrorMessage('Mill formed! Select opponent piece to capture');
            return;
          }
        }

        // No mill formed, place piece normally
        console.log(`Placing piece at position ${position}`);
        setTransactionPending({ type: 'place', to: position, message: 'Placing piece...' });

        // Create signed contract only when needed for transaction
        const millContractWithSigner = await createContract(contractAddresses.Mill, Mill.abi, true);
        const tx = await millContractWithSigner.interact(gameID, position);
        await tx.wait();
        setJustCompletedMove(true); // Force "waiting" display until next poll
        // Transaction pending will be cleared by handleGameUpdate when event is received
        setErrorMessage('');
        setSelectedPosition(null);
        return;
      }

      // Movement phase
      if (gameState.phase === 1) {
        if (moveFromPosition === null) {
          // Select source position
          if (gameState.board[position].toLowerCase() !== userState.userAddress.toLowerCase()) {
            setErrorMessage('Select one of your pieces to move');
            return;
          }
          setMoveFromPosition(position);
          setErrorMessage('Now select where to move this piece');
          return;
        } else {
          // Select destination position
          if (gameState.board[position] !== '0x0000000000000000000000000000000000000000') {
            setErrorMessage('Destination is occupied');
            setMoveFromPosition(null);
            return;
          }

          // Check if this move would form a mill
          const wouldFormMill = await checkWouldFormMill(position, moveFromPosition);
          
          if (wouldFormMill) {
            // Get capturable positions
            const userIsPlayer1 = gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
            const capturable = await millContractToUse.getCaptureablePositions(gameID, userIsPlayer1);
            
            if (capturable.length > 0) {
              // Enter capture mode
              setPendingMove({ type: 'move', from: moveFromPosition, to: position });
              setCaptureablePositions(capturable.map(pos => Number(pos)));
              setCaptureMode(true);
              setErrorMessage('Mill formed! Select opponent piece to capture');
              return;
            }
          }

          // No mill formed, move piece normally
          console.log(`Moving piece from ${moveFromPosition} to ${position}`);
          setTransactionPending({ type: 'move', from: moveFromPosition, to: position, message: 'Moving piece...' });

          // Create signed contract only when needed for transaction
          const millContractWithSigner = await createContract(contractAddresses.Mill, Mill.abi, true);
          const tx = await millContractWithSigner.movePiece(gameID, moveFromPosition, position);
          await tx.wait();
          setJustCompletedMove(true); // Force "waiting" display until next poll
          // Transaction pending will be cleared by handleGameUpdate when event is received
          setErrorMessage('');
          setMoveFromPosition(null);
          setSelectedPosition(null);
          return;
        }
      }

    } catch (error) {
      console.error('Failed to make move:', error);
      setErrorMessage('Failed to make move: ' + error.message);
      setMoveFromPosition(null);
      setSelectedPosition(null);
      setTransactionPending(null);
      // Clear capture mode on error
      setCaptureMode(false);
      setPendingMove(null);
      setCaptureablePositions([]);
    }
  };

  const checkWouldFormMill = async (toPosition, fromPosition) => {
    // Simulate the move on a copy of the board to check for mill formation
    const boardCopy = [...gameState.board];
    
    // If it's a move, remove piece from source position
    if (fromPosition !== null) {
      boardCopy[fromPosition] = '0x0000000000000000000000000000000000000000';
    }
    
    // Place piece at destination
    boardCopy[toPosition] = userState.userAddress;
    
    // Check mill lines that include the destination position (must match contract mill lines)
    const millLines = [
      // Outer square horizontal/vertical
      [0,1,2], [2,3,4], [4,5,6], [6,7,0],  // sides
      // Middle square horizontal/vertical  
      [8,9,10], [10,11,12], [12,13,14], [14,15,8], // sides
      // Inner square horizontal/vertical
      [16,17,18], [18,19,20], [20,21,22], [22,23,16], // sides
      // Cross connections
      [1,9,17], [3,11,19], [5,13,21], [7,15,23] // middle of each side
    ];
    
    for (const line of millLines) {
      if (line.includes(toPosition)) {
        if (boardCopy[line[0]].toLowerCase() === userState.userAddress.toLowerCase() &&
            boardCopy[line[1]].toLowerCase() === userState.userAddress.toLowerCase() &&
            boardCopy[line[2]].toLowerCase() === userState.userAddress.toLowerCase()) {
          return true;
        }
      }
    }
    
    return false;
  };

  const executeMoveWithCapture = async (move, capturePosition) => {
    try {
      // Create signed contract only when needed for transaction
      const millContractWithSigner = await createContract(contractAddresses.Mill, Mill.abi, true);
      
      if (move.type === 'place') {
        console.log(`Placing piece at position ${move.position} and capturing at ${capturePosition}`);
        setTransactionPending({
          type: 'place',
          to: move.position,
          capture: capturePosition,
          message: 'Placing piece and capturing...'
        });
        const tx = await millContractWithSigner.interactWithCapture(gameID, move.position, capturePosition);
        await tx.wait();
        // Transaction pending will be cleared by handleGameUpdate when event is received
      } else if (move.type === 'move') {
        console.log(`Moving piece from ${move.from} to ${move.to} and capturing at ${capturePosition}`);
        setTransactionPending({
          type: 'move',
          from: move.from,
          to: move.to,
          capture: capturePosition,
          message: 'Moving piece and capturing...'
        });
        const tx = await millContractWithSigner.movePieceWithCapture(gameID, move.from, move.to, capturePosition);
        await tx.wait();
        // Transaction pending will be cleared by handleGameUpdate when event is received
      }

      // Force "waiting" display until next poll updates the game state
      setJustCompletedMove(true);
      
      // Clear capture mode
      setCaptureMode(false);
      setPendingMove(null);
      setCaptureablePositions([]);
      setMoveFromPosition(null);
      setSelectedPosition(null);
      setErrorMessage('');
      
    } catch (error) {
      console.error('Failed to execute move with capture:', error);
      setErrorMessage('Failed to execute move: ' + error.message);
      setTransactionPending(null);
      
      // Clear capture mode on error
      setCaptureMode(false);
      setPendingMove(null);
      setCaptureablePositions([]);
      setMoveFromPosition(null);
      setSelectedPosition(null);
    }
  };

  const getStatusMessage = () => {
    // Show pending transaction message if transaction is in progress
    if (transactionPending) {
      return transactionPending.message;
    }
    
    // Show capture mode message
    if (captureMode) {
      return 'Mill formed! Click on an opponent piece to capture';
    }
    
    if (gameState.gameFinished) {
      if (gameState.status === '2') return 'Game ended in draw';
      if (gameState.status === '3') return 'Player 1 won';
      if (gameState.status === '4') return 'Player 2 won';
      if (gameState.status === '5') return 'Game killed';
      return 'Game finished';
    }
    
    // Check if it's user's turn
    const isUserTurn = userState.userAddress && 
                      gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase();
    
    if (gameState.phase === 0) {
      if (isUserTurn) {
        return 'Your turn - Click empty position to place piece';
      } else {
        return `${gameState.status === '0' ? 'Player 1' : 'Player 2'} to place piece`;
      }
    } else {
      if (isUserTurn) {
        if (moveFromPosition === null) {
          return 'Your turn - Click your piece to move';
        } else {
          return 'Select destination for your piece';
        }
      } else {
        return `${gameState.status === '0' ? 'Player 1' : 'Player 2'} to move`;
      }
    }
  };

  const getUserRole = () => {
    if (!userState.userAddress) return 'Connect wallet';
    if (gameState.player1.toLowerCase() === userState.userAddress.toLowerCase()) return 'Player 1';
    if (gameState.player2.toLowerCase() === userState.userAddress.toLowerCase()) return 'Player 2';
    return 'Spectator';
  };

  const getWinnerMessage = () => {
    if (!gameState.gameFinished) return '';
    
    if (gameState.status === '2') return 'Game draw!';
    if (gameState.status === '5') return 'Game was terminated';
    
    const userIsPlayer1 = gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
    const userIsPlayer2 = gameState.player2.toLowerCase() === userState.userAddress.toLowerCase();
    
    if (gameState.status === '3') { // Player 1 won
      if (userIsPlayer1) return 'You won!';
      if (userIsPlayer2) return 'You lost!';
      return 'Player 1 won!';
    }
    
    if (gameState.status === '4') { // Player 2 won
      if (userIsPlayer1) return 'You lost!';
      if (userIsPlayer2) return 'You won!';
      return 'Player 2 won!';
    }
    
    return '';
  };

  const renderBoard = () => {
    // Determine if it's the player's turn and they can interact
    const isPlayerTurn = userState.userAddress &&
                        gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase() &&
                        !gameState.gameFinished;
    
    // Get valid move targets if a piece is selected for moving
    const validMoveTargets = getValidMoveTargets(moveFromPosition);
    
    return (
      <div className="flex justify-center">
        <svg width="400" height="400" viewBox="0 0 400 400" className="border border-gray-300">
          {/* Board lines */}
          <g stroke="#374151" strokeWidth="2" fill="none">
            {/* Outer square */}
            <rect x="50" y="50" width="300" height="300" />
            {/* Middle square */}
            <rect x="100" y="100" width="200" height="200" />
            {/* Inner square */}
            <rect x="150" y="150" width="100" height="100" />
            
            {/* Connecting lines */}
            <line x1="200" y1="50" x2="200" y2="150" />  {/* top */}
            <line x1="350" y1="200" x2="250" y2="200" /> {/* right */}
            <line x1="200" y1="350" x2="200" y2="250" /> {/* bottom */}
            <line x1="50" y1="200" x2="150" y2="200" />  {/* left */}
          </g>
          
          {/* Connecting line for pending moves */}
          {transactionPending?.type === 'move' && transactionPending.from !== undefined && transactionPending.to !== undefined && (
            <line
              x1={boardPositions[transactionPending.from].x}
              y1={boardPositions[transactionPending.from].y}
              x2={boardPositions[transactionPending.to].x}
              y2={boardPositions[transactionPending.to].y}
              stroke="#eab308"
              strokeWidth="6"
              strokeDasharray="5,5"
              className="animate-pulse"
            />
          )}
          
          {/* Yellow edge highlighting for mill-forming moves during capture mode */}
          {captureMode && pendingMove?.type === 'move' && pendingMove.from !== undefined && pendingMove.to !== undefined && (
            <line
              x1={boardPositions[pendingMove.from].x}
              y1={boardPositions[pendingMove.from].y}
              x2={boardPositions[pendingMove.to].x}
              y2={boardPositions[pendingMove.to].y}
              stroke="#eab308"
              strokeWidth="6"
              strokeDasharray="5,5"
              className="animate-pulse"
            />
          )}
          
          {/* Yellow edge highlighting for last move (only for actual moves, not placements) */}
          {showLastMoveIndicators && lastMovePositions.length === 2 && lastMoveType === 'move' && (
            <line
              x1={boardPositions[lastMovePositions[0]].x}
              y1={boardPositions[lastMovePositions[0]].y}
              x2={boardPositions[lastMovePositions[1]].x}
              y2={boardPositions[lastMovePositions[1]].y}
              stroke="#eab308"
              strokeWidth="6"
              strokeDasharray="5,5"
            />
          )}
          
          {/* Position circles */}
          {boardPositions.map((pos, index) => {
            const piece = gameState.board[index];
            const isEmpty = piece === '0x0000000000000000000000000000000000000000';
            const isPlayer1Piece = piece.toLowerCase() === gameState.player1.toLowerCase();
            const isPlayer2Piece = piece.toLowerCase() === gameState.player2.toLowerCase();
            
            // Selection states
            const isSelected = selectedPosition === index || moveFromPosition === index;
            const isPendingSource = transactionPending?.from === index;
            const isPendingDestination = transactionPending?.to === index;
            const isPendingCapture = transactionPending?.capture === index;
            const isMillFormingDestination = captureMode && pendingMove?.type === 'move' && pendingMove.to === index;
            const isMillFormingSource = captureMode && pendingMove?.type === 'move' && pendingMove.from === index;
            const isMillFormingPlacement = captureMode && pendingMove?.type === 'place' && pendingMove.position === index;
            const isLastMovePosition = showLastMoveIndicators && lastMovePositions.includes(index);
            
            // Enhanced pending transaction pulsing - any affected position should pulse
            // const isPendingTransaction = isPendingSource || isPendingDestination || isPendingCapture; // Currently not used but defined for future expansion
            
            // Valid target states for both phases
            const isValidMoveTarget = moveFromPosition !== null && validMoveTargets.includes(index);
            const isValidPlacementTarget = gameState.phase === 0 && isEmpty &&
              userState.userAddress && gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase() &&
              !gameState.gameFinished && !transactionPending && !captureMode;
            const isOwnPieceMovable = gameState.phase === 1 && moveFromPosition === null &&
              userState.userAddress && piece.toLowerCase() === userState.userAddress.toLowerCase() &&
              gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase() &&
              !gameState.gameFinished && !transactionPending && !captureMode;
            const isInvalidTarget = moveFromPosition !== null && !isEmpty && !isValidMoveTarget && !isSelected;
            
            // Color and styling logic
            let fillColor = '#ffffff';
            let strokeColor = '#374151';
            let strokeWidth = 2; // FIXED: Back to normal base thickness
            let opacity = 1;
            let extraClasses = '';
            
            // Base piece colors
            if (isPlayer1Piece) {
              fillColor = '#3b82f6'; // blue
            } else if (isPlayer2Piece) {
              fillColor = '#ef4444'; // red
            }
            
            // Pending placement should show player's color immediately (optimistic update)
            if (transactionPending?.type === 'place' && transactionPending?.to === index) {
              const currentPlayerIsPlayer1 = userState.userAddress && 
                gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
              fillColor = currentPlayerIsPlayer1 ? '#3b82f6' : '#ef4444'; // Show placing player's color
            }
            
            // Pending move destination should show player's color immediately (optimistic update)
            if (transactionPending?.type === 'move' && transactionPending?.to === index) {
              const currentPlayerIsPlayer1 = userState.userAddress && 
                gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
              fillColor = currentPlayerIsPlayer1 ? '#3b82f6' : '#ef4444'; // Show moving player's color
            }
            
            // Mill-forming destination should show player's color during capture mode  
            if (isMillFormingDestination && captureMode) {
              const currentPlayerIsPlayer1 = userState.userAddress && 
                gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
              fillColor = currentPlayerIsPlayer1 ? '#3b82f6' : '#ef4444'; // Show moving player's color
            }
            
            // Additional check: Ensure mill-forming move destinations show player color during capture
            if (captureMode && pendingMove?.type === 'move' && pendingMove.to === index) {
              const currentPlayerIsPlayer1 = userState.userAddress && 
                gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
              fillColor = currentPlayerIsPlayer1 ? '#3b82f6' : '#ef4444'; // Show moving player's color
            }
            
            // Mill-forming placement should show player's color during capture mode
            if (isMillFormingPlacement && captureMode) {
              const currentPlayerIsPlayer1 = userState.userAddress && 
                gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
              fillColor = currentPlayerIsPlayer1 ? '#3b82f6' : '#ef4444'; // Show placing player's color
            }
            
            // Selection highlighting (hidden in capture mode)
            if (!captureMode && (isSelected || isPendingSource)) {
              strokeColor = '#eab308'; // yellow ring for selection and pending
              strokeWidth = 8; // DOUBLED: was 4, now 8
              if (isPendingSource) {
                extraClasses += ' animate-pulse'; // pulse for pending transactions
              }
            }
            
            // Pending destination highlighting (hidden in capture mode)
            if (!captureMode && isPendingDestination) {
              strokeColor = '#eab308'; // yellow ring for pending destination (matches move theme)
              strokeWidth = 8; // DOUBLED: was 4, now 8
              extraClasses += ' animate-pulse'; // pulse for pending transactions
            }
            
            // Pending capture highlighting (during transaction processing)
            if (isPendingCapture) {
              strokeColor = '#eab308'; // yellow ring for piece being captured (consistent with other pending states)
              strokeWidth = 8; // DOUBLED: was 4, now 8
              extraClasses += ' animate-pulse'; // pulse animation to show it's being processed
              // Keep original piece color (red for enemy, blue for own) - don't override fillColor
            }
            
            // Valid move target indication (hidden in capture mode)
            if (!captureMode && isValidMoveTarget && isEmpty) {
              strokeColor = '#10b981'; // green tint for valid targets
              strokeWidth = 6; // DOUBLED: was 3, now 6
            }
            
            // Valid placement target indication (placement phase, player's turn)
            if (isValidPlacementTarget) {
              strokeColor = '#10b981'; // green tint for valid placement targets
              strokeWidth = 6; // Same as move targets for consistency
            }
            
            // Own movable pieces indication (movement phase, no piece selected)
            if (isOwnPieceMovable) {
              strokeColor = '#34d399'; // lighter green hint for own pieces
              strokeWidth = 5; // Slightly thinner than targets for subtlety
            }
            
            // Capturable piece highlighting (only in capture mode)
            if (captureMode && captureablePositions.includes(index)) {
              // If this is also part of a mill formation, use yellow instead of green
              if (isMillFormingDestination || isMillFormingSource || isMillFormingPlacement) {
                strokeColor = '#eab308'; // yellow ring for mill-forming capturable pieces
              } else {
                strokeColor = '#10b981'; // green ring for other capturable pieces
              }
              strokeWidth = 8; // DOUBLED: was 4, now 8
              extraClasses += ' animate-pulse'; // pulse animation
            }
            
            // Mill-forming destination highlighting (show where piece moved to form mill)
            // ALWAYS show mill-forming move, even during capture mode
            if (isMillFormingDestination) {
              strokeColor = '#eab308'; // yellow ring for mill-forming destination (part of current move)
              strokeWidth = 8; // DOUBLED: was 4, now 8
              extraClasses += ' animate-pulse'; // pulse animation to match capturable pieces
            }
            
            // Mill-forming source highlighting (show where piece moved from)
            // ALWAYS show mill-forming move, even during capture mode
            if (isMillFormingSource) {
              strokeColor = '#eab308'; // yellow ring for mill-forming source (part of current move)
              strokeWidth = 8; // DOUBLED: was 4, now 8
              extraClasses += ' animate-pulse'; // pulse animation to match other indicators
            }
            
            // Mill-forming placement highlighting (show where piece was placed to form mill)
            if (isMillFormingPlacement) {
              strokeColor = '#eab308'; // yellow ring for mill-forming placement (part of current move)
              strokeWidth = 8; // DOUBLED: was 4, now 8
              extraClasses += ' animate-pulse'; // pulse animation during capture selection and tx
            }
            
            // Last move indicator highlighting (show positions affected by last move)
            // ALWAYS show last move - removed conditional checks to ensure it's always visible
            if (isLastMovePosition && !isPendingSource && !isPendingDestination && !isPendingCapture) {
              strokeColor = '#eab308'; // yellow ring for last move positions (ALWAYS)
              strokeWidth = 6; // DOUBLED: was 3, now 6
              // Remove pulse animation for last move as it should be persistent indicator
            }
            
            // Invalid target dimming (when piece is selected for moving, but not in capture mode)
            if (isInvalidTarget && !captureMode) {
              opacity = 0.4;
            }
            
            // In capture mode, ensure capturable pieces are full opacity
            if (captureMode && captureablePositions.includes(index)) {
              opacity = 1;
            }
            
            // Conditional interactivity - only when it's player's turn and on valid targets
            if (isPlayerTurn) {
              // Only add cursor-pointer and hover effects for valid interactions
              const isValidInteraction = isValidPlacementTarget || isValidMoveTarget || 
                (gameState.phase === 1 && moveFromPosition === null && isOwnPieceMovable) ||
                (captureMode && captureablePositions.includes(index));
              
              if (isValidInteraction) {
                extraClasses = 'cursor-pointer';
                
                // Add hover preview with player color for valid targets
                const isPlayer1 = gameState.player1.toLowerCase() === userState.userAddress.toLowerCase();
                if (isValidPlacementTarget || isValidMoveTarget) {
                  if (isPlayer1) {
                    extraClasses += ' mill-position-player1-hover-valid'; // Player 1 blue semi-transparent hover
                  } else {
                    extraClasses += ' mill-position-player2-hover-valid'; // Player 2 red semi-transparent hover
                  }
                } else if (isOwnPieceMovable) {
                  // Own piece selection - just slight opacity change
                  extraClasses += ' mill-position-opacity-hover';
                } else if (captureMode && captureablePositions.includes(index)) {
                  // Capturable pieces - no hover color change, they keep their enemy piece color
                  // Just add cursor pointer, no color change on hover
                }
              }
              // No hover effects for invalid positions - they stay as is
            }
            
            // Render marking circle first (background), then piece circle on top
            const hasMarking = strokeColor !== '#374151' || strokeWidth !== 2; // Has special marking
            
            // Separate classes: base circle gets interaction classes, marking circle gets animation classes
            const baseClasses = extraClasses.replace(' animate-pulse', ''); // Remove pulse from base
            const markingClasses = extraClasses; // Keep all classes including pulse for marking
            
            return (
              <g key={index}>
                {/* Base circle with black border (bottom layer) */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r="12"
                  fill={fillColor}
                  stroke="#374151"
                  strokeWidth="2"
                  opacity={opacity}
                  className={baseClasses}
                  onClick={() => handlePositionClick(index)}
                />
                
                {/* Marking circle on top of black border (middle layer) */}
                {hasMarking && (
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r="12"
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    opacity={opacity}
                    className={markingClasses}
                    pointerEvents="none"
                  />
                )}
              </g>
            );
          })}
          
          {/* Position numbers for debugging */}
          {boardPositions.map((pos, index) => (
            <text
              key={`label-${index}`}
              x={pos.x}
              y={pos.y - 20}
              textAnchor="middle"
              className="text-xs fill-gray-500 pointer-events-none"
            >
              {index}
            </text>
          ))}
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (errorMessage && !gameState.player1) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          Error: {errorMessage}
        </div>
      </div>
    );
  }

  if (!isWalletInitialized) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Initializing...</h1>
        <p className="text-gray-500">Connecting to wallet...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <style>
        {`
          .collapsible-button {
            background: white !important;
            background-color: white !important;
            background-image: none !important;
            color: #374151 !important;
            border: none !important;
            padding: 0 !important;
            border-radius: 0 !important;
          }
          .collapsible-container {
            background-color: white !important;
            border-radius: 0.5rem !important;
            padding: 1.5rem !important;
            margin-bottom: 1.5rem !important;
            border: none !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          }
          .mill-position-player1-hover-valid:hover {
            fill: rgba(59, 130, 246, 0.8) !important; /* More solid blue for Player 1 valid targets */
          }
          .mill-position-player2-hover-valid:hover {
            fill: rgba(239, 68, 68, 0.8) !important; /* More solid red for Player 2 valid targets */
          }
          .mill-position-opacity-hover:hover {
            opacity: 0.8;
          }
        `}
      </style>
      <div className="max-w-4xl mx-auto">
        {/* Top Navigation Bar */}
        <div className="flex justify-between items-center mb-6">
          <Link
            to="/1on1"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition"
          >
            ← Back to Games
          </Link>

          <TopRightBar
            onSwitchWallet={handleSwitchWallet}
            onWalletDisconnect={handleWalletDisconnect}
            showInTestMode={true}
          />
        </div>

        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          Mill Game #{gameID}
        </h1>

        {/* Game Rules - Collapsible */}
        <div className="collapsible-container">
          <button 
            className="collapsible-button"
            onClick={() => setRulesCollapsed(!rulesCollapsed)}
            style={{ 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              fontSize: '1.25rem', 
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <span>How Mill (Nine Men's Morris) Works</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${rulesCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            rulesCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100 mt-3'
          }`}>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li>Each player has <strong>9 pieces</strong> to place on the board's 24 positions</li>
              <li><strong>Phase 1 (Placement):</strong> Take turns placing pieces on empty positions</li>
              <li><strong>Mills:</strong> Form lines of 3 pieces horizontally or vertically to capture opponent pieces</li>
              <li><strong>Capturing:</strong> When you form a mill, remove one opponent piece (not in a mill)</li>
              <li><strong>Phase 2 (Movement):</strong> After all pieces placed, move pieces to adjacent positions</li>
              <li><strong>Wild Rule:</strong> When you have only 3 pieces left, you can move to any empty position</li>
              <li><strong>Win Condition:</strong> Reduce opponent to less than 3 pieces or block all their moves</li>
              <li>Random player starts first (50/50 chance at game acceptance)</li>
              <li>🔍 Find the contract info and verified code here: <AddressDisplayLong address={contractAddresses.Mill} useExplorerLink={true} /></li>
            </ul>
          </div>
        </div>

        {/* Game Status */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Game Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-lg font-medium">{getStatusMessage()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Your Role</p>
              <p className="text-lg font-medium">{getUserRole()}</p>
            </div>
          </div>
          
          {/* Game Phase Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
            <div>
              <p className="text-gray-600">Phase</p>
              <p className="font-medium">{gameState.phase === 0 ? 'Placement' : 'Movement'}</p>
            </div>
            <div>
              <p className="text-gray-600">First Player</p>
              <p className="font-medium">{gameState.firstPlayer === gameState.player1 ? 'Player 1' : 'Player 2'}</p>
            </div>
          </div>
          
          {/* Detailed Piece Tracking */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Player 1 (Blue) Pieces</h4>
              <div className="space-y-1 text-sm">
                {gameState.phase === 0 ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Left to place:</span>
                      <span className="font-medium">{9 - gameState.player1PlacedPieces}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">On board:</span>
                      <span className="font-medium">{gameState.player1Pieces}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">On board:</span>
                      <span className="font-medium">{gameState.player1Pieces}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lost:</span>
                      <span className="font-medium">{gameState.player1PlacedPieces - gameState.player1Pieces}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Player 2 (Red) Pieces</h4>
              <div className="space-y-1 text-sm">
                {gameState.phase === 0 ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Left to place:</span>
                      <span className="font-medium">{9 - gameState.player2PlacedPieces}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">On board:</span>
                      <span className="font-medium">{gameState.player2Pieces}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">On board:</span>
                      <span className="font-medium">{gameState.player2Pieces}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lost:</span>
                      <span className="font-medium">{gameState.player2PlacedPieces - gameState.player2Pieces}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Game Board */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Game Board</h2>
          
          <div className="flex flex-col items-center">
            {renderBoard()}
            
            {/* Game Instructions */}
            {!gameState.gameFinished && (
              <div className="text-center mt-4">
                {transactionPending ? (
                  <div>
                    <p className="text-lg font-semibold text-blue-600">Transaction Pending</p>
                    <p className="text-sm text-gray-600">{transactionPending.message}</p>
                  </div>
                ) : justCompletedMove ? (
                  <p className="text-lg font-medium text-gray-600">Waiting for opponent's move...</p>
                ) : gameState.currentPlayer.toLowerCase() === userState.userAddress.toLowerCase() ? (
                  <div>
                    <p className="text-lg font-semibold text-green-600">Your turn!</p>
                    {!captureMode && (
                      gameState.phase === 0 ? (
                        <p className="text-sm text-gray-600">Click an empty position to place a piece</p>
                      ) : moveFromPosition === null ? (
                        <p className="text-sm text-gray-600">Click one of your pieces to move it</p>
                      ) : (
                        <p className="text-sm text-gray-600">Click an empty position to move there</p>
                      )
                    )}
                  </div>
                ) : (
                  <p className="text-lg font-medium text-gray-600">Waiting for opponent's move...</p>
                )}
              </div>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="text-center mt-4">
                <p className="text-red-600 font-medium">{errorMessage}</p>
              </div>
            )}

            {/* Reset selection buttons */}
            {(moveFromPosition !== null || selectedPosition !== null || captureMode) && (
              <div className="text-center mt-4">
                <button
                  onClick={() => {
                    setMoveFromPosition(null);
                    setSelectedPosition(null);
                    setCaptureMode(false);
                    setPendingMove(null);
                    setCaptureablePositions([]);
                    setErrorMessage('');
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  {captureMode ? 'Cancel Mill Capture' : 'Cancel Selection'}
                </button>
              </div>
            )}

            {/* Game Result */}
            {gameState.gameFinished && getWinnerMessage() && (
              <div className={`text-center p-4 rounded-lg mt-4 ${
                getWinnerMessage() === 'You won!' ? 'bg-green-50' :
                getWinnerMessage() === 'You lost!' ? 'bg-red-50' :
                getWinnerMessage() === 'Game draw!' ? 'bg-yellow-50' : 'bg-gray-50'
              }`}>
                <p className={`text-lg font-medium ${
                  getWinnerMessage() === 'You won!' ? 'text-green-600' :
                  getWinnerMessage() === 'You lost!' ? 'text-red-600' :
                  getWinnerMessage() === 'Game draw!' ? 'text-yellow-600' : 'text-gray-600'
                }`}>{getWinnerMessage()}</p>
              </div>
            )}
          </div>
        </div>

        {/* Players Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Player 1 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 1 (Blue)</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player1} useExplorerLink={true} /></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bet Amount</p>
                <p className="text-lg font-medium">{formatEth(gameState.contribution1)} POL</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pieces on Board</p>
                <p className="text-lg font-medium">{gameState.player1Pieces}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pieces Placed</p>
                <p className="text-lg font-medium">{gameState.player1PlacedPieces}/9</p>
              </div>
              {gameState.winner === gameState.player1 && (
                <div className="text-green-600 font-semibold">🎉 Winner!</div>
              )}
            </div>
          </div>

          {/* Player 2 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 2 (Red)</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player2} useExplorerLink={true} /></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bet Amount</p>
                <p className="text-lg font-medium">{formatEth(gameState.contribution2)} POL</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pieces on Board</p>
                <p className="text-lg font-medium">{gameState.player2Pieces}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pieces Placed</p>
                <p className="text-lg font-medium">{gameState.player2PlacedPieces}/9</p>
              </div>
              {gameState.winner === gameState.player2 && (
                <div className="text-green-600 font-semibold">🎉 Winner!</div>
              )}
            </div>
          </div>
        </div>

        {/* Game Details */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-3 text-gray-700">Game Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-600">Game ID</p>
              <p className="font-mono">{gameID}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pot</p>
              <p className="text-lg font-medium">
                {formatEth((ethersUtils.toBN(gameState.contribution1).add(ethersUtils.toBN(gameState.contribution2))).toString())} POL
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Move</p>
              <p className="text-sm">
                {gameState.lastMoveTime > 0
                  ? new Date(gameState.lastMoveTime * 1000).toLocaleString()
                  : 'No moves yet'
                }
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Contract Address</p>
              <div className="font-mono text-xs"><AddressDisplayLong address={contractAddresses.Mill} useExplorerLink={true} /></div>
            </div>
          </div>

          {/* Transaction Details - Unified Component */}
          <GameTransactionHistory
            gameId={gameID}
            gameMakerContract={gameMakerContract}
            gameContract={millContract}
            gameEvents={millGameEvents}
            player1={gameState.player1}
            player2={gameState.player2}
          />
        </div>
      </div>
    </div>
  );
};

export default MillGame;